'use client';

import React, { useState } from 'react';
import AppLayout from '@/components/AppLayout';
import {
  Megaphone, TrendingUp, Users, MousePointerClick, DollarSign,
  ArrowUpRight, ArrowDownRight, Mail, Globe, Share2, Search,
  Eye, Heart, MessageSquare, RefreshCw, Filter, Download,
  Target, Zap, BarChart2,
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, AreaChart, Area,  } from 'recharts';

// ── Data ──────────────────────────────────────────────────────────────────────

const kpis = [
  { label: 'Total Impressions', value: '2.4M', change: '+18.3%', up: true, icon: Eye, color: 'text-primary', bg: 'bg-primary/10' },
  { label: 'Leads Generated', value: '1,284', change: '+12.7%', up: true, icon: Users, color: 'text-success', bg: 'bg-success/10' },
  { label: 'Avg. CTR', value: '4.8%', change: '+0.6%', up: true, icon: MousePointerClick, color: 'text-amber-400', bg: 'bg-amber-400/10' },
  { label: 'Total Ad Spend', value: '$18,400', change: '-4.2%', up: false, icon: DollarSign, color: 'text-cyan-400', bg: 'bg-cyan-400/10' },
  { label: 'Blended ROI', value: '312%', change: '+28%', up: true, icon: TrendingUp, color: 'text-violet-400', bg: 'bg-violet-400/10' },
  { label: 'Conversions', value: '387', change: '+9.1%', up: true, icon: Target, color: 'text-rose-400', bg: 'bg-rose-400/10' },
];

const campaigns = [
  { id: 1, name: 'Q3 BPO Lead Gen', channel: 'Google Ads', status: 'Active', budget: 5200, spent: 3840, leads: 214, cpl: 17.9, roi: 340, ctr: 5.2, conv: 98 },
  { id: 2, name: 'Social Brand Awareness', channel: 'Meta Ads', status: 'Active', budget: 3000, spent: 2100, leads: 187, cpl: 11.2, roi: 280, ctr: 3.8, conv: 62 },
  { id: 3, name: 'Email Nurture Series', channel: 'Email', status: 'Active', budget: 800, spent: 620, leads: 312, cpl: 2.0, roi: 520, ctr: 22.4, conv: 89 },
  { id: 4, name: 'SEO Content Push', channel: 'Organic', status: 'Active', budget: 1200, spent: 900, leads: 276, cpl: 3.3, roi: 410, ctr: 6.1, conv: 74 },
  { id: 5, name: 'LinkedIn B2B Outreach', channel: 'LinkedIn', status: 'Paused', budget: 2400, spent: 1980, leads: 143, cpl: 13.8, roi: 195, ctr: 2.9, conv: 38 },
  { id: 6, name: 'Retargeting — Web Visitors', channel: 'Google Ads', status: 'Active', budget: 1800, spent: 1240, leads: 152, cpl: 8.2, roi: 290, ctr: 7.4, conv: 26 },
];

const channelData = [
  { channel: 'Google Ads', leads: 366, spend: 5080, roi: 315, color: '#6366f1' },
  { channel: 'Email', leads: 312, spend: 620, roi: 520, color: '#22d3ee' },
  { channel: 'Organic SEO', leads: 276, spend: 900, roi: 410, color: '#34d399' },
  { channel: 'Meta Ads', leads: 187, spend: 2100, roi: 280, color: '#fbbf24' },
  { channel: 'LinkedIn', leads: 143, spend: 1980, roi: 195, color: '#a78bfa' },
];

const leadSources = [
  { name: 'Google Ads', value: 366, color: '#6366f1' },
  { name: 'Email', value: 312, color: '#22d3ee' },
  { name: 'Organic SEO', value: 276, color: '#34d399' },
  { name: 'Meta Ads', value: 187, color: '#fbbf24' },
  { name: 'LinkedIn', value: 143, color: '#a78bfa' },
];

const engagementTrend = [
  { week: 'W1', impressions: 380, clicks: 18, leads: 42 },
  { week: 'W2', impressions: 420, clicks: 22, leads: 51 },
  { week: 'W3', impressions: 390, clicks: 19, leads: 47 },
  { week: 'W4', impressions: 510, clicks: 28, leads: 68 },
  { week: 'W5', impressions: 480, clicks: 25, leads: 62 },
  { week: 'W6', impressions: 560, clicks: 31, leads: 79 },
  { week: 'W7', impressions: 610, clicks: 34, leads: 84 },
  { week: 'W8', impressions: 590, clicks: 32, leads: 81 },
];

const roiTrend = [
  { month: 'Mar', roi: 210, spend: 12000 },
  { month: 'Apr', roi: 245, spend: 13500 },
  { month: 'May', roi: 268, spend: 15200 },
  { month: 'Jun', roi: 290, spend: 16800 },
  { month: 'Jul', roi: 305, spend: 17600 },
  { month: 'Aug', roi: 312, spend: 18400 },
];

const engagementStats = [
  { label: 'Avg. Open Rate', value: '38.4%', icon: Mail, change: '+2.1%', up: true },
  { label: 'Social Reach', value: '142K', icon: Share2, change: '+11.8%', up: true },
  { label: 'Organic Traffic', value: '28.6K', icon: Globe, change: '+7.4%', up: true },
  { label: 'Search Impressions', value: '890K', icon: Search, change: '+15.2%', up: true },
  { label: 'Likes & Reactions', value: '4,820', icon: Heart, change: '+6.3%', up: true },
  { label: 'Comments & Replies', value: '1,140', icon: MessageSquare, change: '-1.2%', up: false },
];

const CHART_COLORS = { primary: '#6366f1', success: '#34d399', warning: '#fbbf24', muted: '#334155', cyan: '#22d3ee' };

const statusBadge = (status: string) => {
  const map: Record<string, string> = {
    Active: 'bg-success/15 text-success',
    Paused: 'bg-amber-400/15 text-amber-400',
    Ended: 'bg-muted text-muted-foreground',
  };
  return map[status] ?? 'bg-muted text-muted-foreground';
};

const channelIcon = (ch: string) => {
  const map: Record<string, React.ElementType> = {
    'Google Ads': Search,
    'Meta Ads': Share2,
    'Email': Mail,
    'Organic': Globe,
    'LinkedIn': Users,
  };
  return map[ch] ?? Megaphone;
};

// ── Page ──────────────────────────────────────────────────────────────────────

export default function MarketingPage() {
  const [activeFilter, setActiveFilter] = useState<'All' | 'Active' | 'Paused'>('All');

  const filtered = campaigns.filter((c) => activeFilter === 'All' || c.status === activeFilter);

  return (
    <AppLayout>
      <div className="min-h-screen bg-background px-4 sm:px-6 py-6 max-w-screen-2xl mx-auto">

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-foreground flex items-center gap-2">
              <Megaphone size={22} className="text-primary" />
              Digital Marketing
            </h1>
            <p className="text-sm text-muted-foreground mt-0.5">Campaign performance, channel metrics, lead attribution &amp; ROI</p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button className="flex items-center gap-1.5 bg-card border border-border text-muted-foreground px-3 py-2 rounded-lg text-sm hover:text-foreground hover:bg-muted transition-colors">
              <RefreshCw size={14} />
              <span className="hidden sm:inline">Refresh</span>
            </button>
            <button className="flex items-center gap-1.5 bg-card border border-border text-foreground px-3 py-2 rounded-lg text-sm font-medium hover:bg-muted transition-colors">
              <Download size={14} />
              <span className="hidden sm:inline">Export</span>
            </button>
          </div>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
          {kpis.map((kpi) => (
            <div key={kpi.label} className="bg-card border border-border rounded-xl p-3 sm:p-4">
              <div className="flex items-center justify-between mb-2">
                <div className={`w-8 h-8 rounded-lg ${kpi.bg} flex items-center justify-center`}>
                  <kpi.icon size={15} className={kpi.color} />
                </div>
                <span className={`text-xs flex items-center gap-0.5 font-medium ${kpi.up ? 'text-success' : 'text-danger'}`}>
                  {kpi.up ? <ArrowUpRight size={11} /> : <ArrowDownRight size={11} />}
                  {kpi.change}
                </span>
              </div>
              <p className="text-lg sm:text-xl font-bold text-foreground">{kpi.value}</p>
              <p className="text-2xs sm:text-xs text-muted-foreground mt-0.5 leading-tight">{kpi.label}</p>
            </div>
          ))}
        </div>

        {/* Charts Row 1: Engagement Trend + Lead Source Pie */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-5">
          {/* Engagement Trend */}
          <div className="lg:col-span-2 bg-card border border-border rounded-xl p-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-semibold text-foreground">Engagement Trend (8 Weeks)</h2>
              <div className="flex items-center gap-3 text-xs text-muted-foreground">
                <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-primary inline-block" />Impressions (K)</span>
                <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-success inline-block" />Leads</span>
              </div>
            </div>
            <ResponsiveContainer width="100%" height={210}>
              <AreaChart data={engagementTrend}>
                <defs>
                  <linearGradient id="impGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={CHART_COLORS.primary} stopOpacity={0.25} />
                    <stop offset="95%" stopColor={CHART_COLORS.primary} stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="leadGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={CHART_COLORS.success} stopOpacity={0.25} />
                    <stop offset="95%" stopColor={CHART_COLORS.success} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke={CHART_COLORS.muted} opacity={0.3} />
                <XAxis dataKey="week" tick={{ fill: '#94a3b8', fontSize: 12 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: '#94a3b8', fontSize: 12 }} axisLine={false} tickLine={false} />
                <Tooltip
                  contentStyle={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 8 }}
                  labelStyle={{ color: '#f1f5f9' }}
                />
                <Area type="monotone" dataKey="impressions" stroke={CHART_COLORS.primary} strokeWidth={2} fill="url(#impGrad)" name="Impressions (K)" />
                <Area type="monotone" dataKey="leads" stroke={CHART_COLORS.success} strokeWidth={2} fill="url(#leadGrad)" name="Leads" />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          {/* Lead Source Attribution */}
          <div className="bg-card border border-border rounded-xl p-5">
            <h2 className="font-semibold text-foreground mb-4">Lead Source Attribution</h2>
            <ResponsiveContainer width="100%" height={150}>
              <PieChart>
                <Pie data={leadSources} cx="50%" cy="50%" innerRadius={42} outerRadius={65} paddingAngle={3} dataKey="value">
                  {leadSources.map((entry, i) => (
                    <Cell key={i} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 8 }}
                  formatter={(v: number) => [`${v} leads`, '']}
                />
              </PieChart>
            </ResponsiveContainer>
            <div className="space-y-2 mt-3">
              {leadSources.map((s) => {
                const total = leadSources.reduce((a, b) => a + b.value, 0);
                const pct = Math.round((s.value / total) * 100);
                return (
                  <div key={s.name} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: s.color }} />
                      <span className="text-xs text-muted-foreground">{s.name}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-medium text-foreground">{s.value}</span>
                      <span className="text-2xs text-muted-foreground w-8 text-right">{pct}%</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Charts Row 2: ROI Trend + Channel Metrics */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-5">
          {/* ROI Trend */}
          <div className="bg-card border border-border rounded-xl p-5">
            <h2 className="font-semibold text-foreground mb-1">ROI Trend</h2>
            <p className="text-xs text-muted-foreground mb-4">6-month blended return on ad spend</p>
            <ResponsiveContainer width="100%" height={160}>
              <LineChart data={roiTrend}>
                <CartesianGrid strokeDasharray="3 3" stroke={CHART_COLORS.muted} opacity={0.3} />
                <XAxis dataKey="month" tick={{ fill: '#94a3b8', fontSize: 12 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: '#94a3b8', fontSize: 12 }} axisLine={false} tickLine={false} tickFormatter={(v) => `${v}%`} />
                <Tooltip
                  contentStyle={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 8 }}
                  labelStyle={{ color: '#f1f5f9' }}
                  formatter={(v: number, name: string) => [name === 'roi' ? `${v}%` : `$${v.toLocaleString()}`, name === 'roi' ? 'ROI' : 'Spend']}
                />
                <Line type="monotone" dataKey="roi" stroke={CHART_COLORS.cyan} strokeWidth={2.5} dot={{ fill: CHART_COLORS.cyan, r: 4 }} name="roi" />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Channel Performance Bars */}
          <div className="lg:col-span-2 bg-card border border-border rounded-xl p-5">
            <h2 className="font-semibold text-foreground mb-4">Channel Performance</h2>
            <div className="space-y-4">
              {channelData.map((ch) => {
                const maxLeads = Math.max(...channelData.map((c) => c.leads));
                const pct = Math.round((ch.leads / maxLeads) * 100);
                return (
                  <div key={ch.channel}>
                    <div className="flex items-center justify-between mb-1.5">
                      <div className="flex items-center gap-2">
                        <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: ch.color }} />
                        <span className="text-sm font-medium text-foreground">{ch.channel}</span>
                      </div>
                      <div className="flex items-center gap-4 text-xs text-muted-foreground">
                        <span><span className="text-foreground font-medium">{ch.leads}</span> leads</span>
                        <span><span className="text-foreground font-medium">${(ch.spend / 1000).toFixed(1)}k</span> spend</span>
                        <span className="text-success font-medium">{ch.roi}% ROI</span>
                      </div>
                    </div>
                    <div className="h-2 bg-muted rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all duration-500"
                        style={{ width: `${pct}%`, background: ch.color }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Engagement Stats */}
        <div className="bg-card border border-border rounded-xl p-5 mb-5">
          <h2 className="font-semibold text-foreground mb-4 flex items-center gap-2">
            <Zap size={16} className="text-amber-400" />
            Engagement Stats
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
            {engagementStats.map((stat) => (
              <div key={stat.label} className="flex flex-col gap-1">
                <div className="flex items-center gap-1.5 text-muted-foreground">
                  <stat.icon size={13} />
                  <span className="text-xs">{stat.label}</span>
                </div>
                <p className="text-lg font-bold text-foreground">{stat.value}</p>
                <span className={`text-xs flex items-center gap-0.5 font-medium ${stat.up ? 'text-success' : 'text-danger'}`}>
                  {stat.up ? <ArrowUpRight size={11} /> : <ArrowDownRight size={11} />}
                  {stat.change}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Campaign Table */}
        <div className="bg-card border border-border rounded-xl p-5">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
            <h2 className="font-semibold text-foreground flex items-center gap-2">
              <BarChart2 size={16} className="text-primary" />
              Campaign Performance
            </h2>
            <div className="flex items-center gap-2">
              <Filter size={14} className="text-muted-foreground" />
              <div className="flex bg-muted/40 border border-border rounded-lg overflow-hidden">
                {(['All', 'Active', 'Paused'] as const).map((f) => (
                  <button
                    key={f}
                    onClick={() => setActiveFilter(f)}
                    className={`px-3 py-1.5 text-xs font-medium transition-colors ${activeFilter === f ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'}`}
                  >
                    {f}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Desktop table */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  {['Campaign', 'Channel', 'Status', 'Budget', 'Spent', 'Leads', 'CPL', 'CTR', 'ROI'].map((h) => (
                    <th key={h} className="text-left pb-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider pr-4 last:pr-0 last:text-right">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {filtered.map((c) => {
                  const CIcon = channelIcon(c.channel);
                  return (
                    <tr key={c.id} className="hover:bg-muted/20 transition-colors">
                      <td className="py-3 pr-4 font-medium text-foreground whitespace-nowrap">{c.name}</td>
                      <td className="py-3 pr-4">
                        <span className="flex items-center gap-1.5 text-muted-foreground text-xs">
                          <CIcon size={13} />
                          {c.channel}
                        </span>
                      </td>
                      <td className="py-3 pr-4">
                        <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${statusBadge(c.status)}`}>{c.status}</span>
                      </td>
                      <td className="py-3 pr-4 text-muted-foreground text-xs">${c.budget.toLocaleString()}</td>
                      <td className="py-3 pr-4">
                        <div>
                          <div className="text-xs text-foreground font-medium">${c.spent.toLocaleString()}</div>
                          <div className="w-20 h-1 bg-muted rounded-full mt-1 overflow-hidden">
                            <div className="h-full bg-primary rounded-full" style={{ width: `${Math.round((c.spent / c.budget) * 100)}%` }} />
                          </div>
                        </div>
                      </td>
                      <td className="py-3 pr-4 text-foreground font-medium text-xs">{c.leads}</td>
                      <td className="py-3 pr-4 text-muted-foreground text-xs">${c.cpl}</td>
                      <td className="py-3 pr-4 text-muted-foreground text-xs">{c.ctr}%</td>
                      <td className="py-3 text-right">
                        <span className={`text-xs font-semibold flex items-center justify-end gap-0.5 ${c.roi >= 300 ? 'text-success' : c.roi >= 200 ? 'text-amber-400' : 'text-muted-foreground'}`}>
                          <ArrowUpRight size={12} />
                          {c.roi}%
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="md:hidden space-y-3">
            {filtered.map((c) => {
              const CIcon = channelIcon(c.channel);
              return (
                <div key={c.id} className="border border-border rounded-lg p-3">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <p className="text-sm font-medium text-foreground">{c.name}</p>
                      <span className="flex items-center gap-1 text-xs text-muted-foreground mt-0.5">
                        <CIcon size={12} />{c.channel}
                      </span>
                    </div>
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${statusBadge(c.status)}`}>{c.status}</span>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-xs">
                    <div><p className="text-muted-foreground">Leads</p><p className="font-semibold text-foreground">{c.leads}</p></div>
                    <div><p className="text-muted-foreground">CPL</p><p className="font-semibold text-foreground">${c.cpl}</p></div>
                    <div><p className="text-muted-foreground">ROI</p><p className={`font-semibold ${c.roi >= 300 ? 'text-success' : 'text-amber-400'}`}>{c.roi}%</p></div>
                  </div>
                  <div className="mt-2">
                    <div className="flex justify-between text-xs text-muted-foreground mb-1">
                      <span>Budget utilization</span>
                      <span>${c.spent.toLocaleString()} / ${c.budget.toLocaleString()}</span>
                    </div>
                    <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                      <div className="h-full bg-primary rounded-full" style={{ width: `${Math.round((c.spent / c.budget) * 100)}%` }} />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

      </div>
    </AppLayout>
  );
}
